import {DefaultAdapter} from './default.adapter';
import {CustomerDocument} from '../interfaces/customer-documents/customer-document';
import {inject} from '@angular/core';
import {AdapterOptions} from '../interfaces/adapter/adapter-options';
import {map} from 'rxjs/operators';
import {SyncException} from '../exceptions/sync.exception';
import {RequestCancelledError} from '../exceptions/request-cancelled.error';
import {DocumentsService} from '../services/core/documents.service';
import {db} from '../tables/db.service';

export class CustomerDocumentsAdapter extends DefaultAdapter<CustomerDocument> {

  /**
   *
   * @private
   */
  private readonly _documentsService: DocumentsService;

  /**
   *
   * @param adapterOptions
   */
  constructor(adapterOptions: AdapterOptions<CustomerDocument>) {
    super(adapterOptions);
    this._documentsService = inject(DocumentsService);
  }

  /**
   *
   * @param customerDocument
   */
  public override async save(customerDocument: CustomerDocument): Promise<CustomerDocument> {
    return this.uploadCustomerDocument(customerDocument);
  }

  /**
   *
   * @param customerDocument
   * @private
   */
  private async uploadCustomerDocument(customerDocument: CustomerDocument): Promise<CustomerDocument> {
    const formData = new FormData();
    formData.append('customerDocument', JSON.stringify(this._toRemote(customerDocument)));

    // The file is only sent when it is actually outstanding – a new document or
    // a replacement. The server swaps the stored file whenever one arrives, so
    // a plain metadata edit must not carry it. Rows written before this field
    // existed have it undefined and keep the previous always-send behaviour.
    if(customerDocument.hasUnsyncedFile !== false) {
      const blob = await this._documentsService.readDocument(customerDocument.file);
      formData.append('file', blob, customerDocument.uuid + '.pdf');
    }

    return new Promise((resolve, reject) => {
      // The async next handler suspends at its await, so complete fires before resolve.
      // The flag prevents that completion from rejecting an already received upload.
      let hasReceivedItem = false;

      this._httpService.post<any>({
        url: [...this.generateUrl(customerDocument), 'upload', customerDocument.uuid],
        query: { v: 2 }
      }, formData, {
        timeout: 20000
      }).pipe(map((_item) => {
        return this._fromObject(_item);
      })).subscribe({
        next: async (_item) => {
          hasReceivedItem = true;
          await this.clearUnsyncedFileFlag(customerDocument);
          resolve(_item);
        },
        error: (error) => {
          // A cancellation must stay recognizable and not be mapped to a SyncException
          if(error instanceof RequestCancelledError) {
            reject(error);
            return;
          }
          console.log(error);
          const syncException = new SyncException('Unknown sync error');
          if(error?.status && error?.message) {
            syncException.code = error.status;
            syncException.message = error.message;
          }
          reject(syncException);
        },
        complete: () => {
          // A completion without emission must still settle the promise
          if(!hasReceivedItem) {
            reject(new RequestCancelledError());
          }
        }
      });
    });

  }

  /**
   * The file reached the server, so following syncs of this row must not send
   * it again. Written straight to the table instead of through the sync table:
   * SyncService compares `modified` to decide whether the row is synchronized,
   * and touching it here would keep the row queued forever.
   *
   * @param customerDocument
   * @private
   */
  private async clearUnsyncedFileFlag(customerDocument: CustomerDocument): Promise<void> {
    if(customerDocument.hasUnsyncedFile === false) {
      return;
    }

    try {
      await db.customerDocuments.update(customerDocument.uuid, { hasUnsyncedFile: false });
    }
    catch(error) {
      console.warn('Could not clear the unsynced file flag:', error);
    }
  }
}